import React from "react";
import { JSBaseMap } from "./JSBaseMap";
import { CHARCOAL, D3PseudoSelect, LIGHT_GREY, LIGHT_GREY_3 } from "../misc";
import { Config } from "../../Config";
import { ColorMappings, GeoFeature, Geojson, SpatialValue } from "./types";
import mapboxgl from "mapbox-gl";
import "./BaseMap.scss";
import { useShapefileContext } from "Components/Context";

const COLOR = {
    BACKGROUND: LIGHT_GREY_3,
    OCEAN: LIGHT_GREY,
    // OCEAN: "#13498C",
    NOT_FOUND_COLOR: LIGHT_GREY,
    CLICK_STROKE_COLOR: CHARCOAL,
    CLICK_STROKE_HIGHLIGHT_EFFECT_COLOR: LIGHT_GREY_3,
    MOUSEOUT_POLYGON_STROKE: LIGHT_GREY,
    MOUSEENTER_POLYGON_STROKE: LIGHT_GREY
};

export interface HoverProperties {}

type HoverCallback = (args: {
    d3Tooltip: D3PseudoSelect;
    features: GeoFeature[];
    key: string;
    value: string | number;
    data: any;

    legendMappings?: MapLegend[];
    meta?: Record<string, unknown>;
}) => void;

export interface MapLegend {
    // label to display next to the color box
    label: string;

    // label within the color box
    boxLabel: string;

    // value to decide the color of the box
    value: string | number;
}

export interface BackButtonMeta {
    text: string;
    onClick: () => void;
}

export interface DataLayer extends DataLayerStyle {
    id: string;
    spatialKey: string;
    source: string;
    "source-layer": string;
    featureGroupingSelection?: string[];
    type: "polygon" | "point";
}

export interface DataLayerStyle {
    radius?: number;

    mouseOutPolygonStroke?: string;
    mouseOutPolygonStrokeWidth?: number;
    mouseOutPolygonStrokeOpacity?: number;
    mouseOutPolygonFillOpacity?: number;

    mouseEnterPolygonStroke?: string;
    mouseEnterPolygonStrokeWidth?: number;
    mouseEnterPolygonStrokeOpacity?: number;
    mouseEnterPolygonFillOpacity?: number;

    clickedPolygonStroke?: string;
    clickedPolygonStrokeOpacity?: number;
    clickedPolygonStrokeWidth?: number;
}

const polygonStyles: (useHighlightEffect?: boolean) => DataLayerStyle = (useHighlightEffect = true) => {
    return {
        radius: undefined,
        mouseOutPolygonStroke: COLOR.MOUSEOUT_POLYGON_STROKE,
        mouseOutPolygonStrokeWidth: 1,
        mouseOutPolygonStrokeOpacity: 1,
        mouseOutPolygonFillOpacity: 1,

        mouseEnterPolygonStroke: COLOR.MOUSEENTER_POLYGON_STROKE,
        mouseEnterPolygonStrokeWidth: 1,
        mouseEnterPolygonStrokeOpacity: 1,
        mouseEnterPolygonFillOpacity: 0.5,

        clickedPolygonStroke: useHighlightEffect ? COLOR.CLICK_STROKE_HIGHLIGHT_EFFECT_COLOR : COLOR.CLICK_STROKE_COLOR,
        clickedPolygonStrokeOpacity: 1,
        clickedPolygonStrokeWidth: 3
    };
};

const disabledHoverPolygonStyles: (useHighlightEffect?: boolean) => DataLayerStyle = (useHighlightEffect = true) => {
    return {
        radius: undefined,
        mouseOutPolygonStroke: COLOR.MOUSEOUT_POLYGON_STROKE,
        mouseOutPolygonStrokeWidth: 1,
        mouseOutPolygonStrokeOpacity: 1,
        mouseOutPolygonFillOpacity: 1,

        mouseEnterPolygonStroke: COLOR.MOUSEENTER_POLYGON_STROKE,
        mouseEnterPolygonStrokeWidth: 1,
        mouseEnterPolygonStrokeOpacity: 1,
        mouseEnterPolygonFillOpacity: 1,

        // clickedPolygonStroke: COLOR.CLICK_STROKE_HIGHLIGHT_EFFECT_COLOR,
        clickedPolygonStroke: useHighlightEffect ? COLOR.CLICK_STROKE_HIGHLIGHT_EFFECT_COLOR : COLOR.CLICK_STROKE_COLOR,
        clickedPolygonStrokeOpacity: 1,
        clickedPolygonStrokeWidth: 3
    };
};

const pointStyle: DataLayerStyle = {
    radius: 10,
    // radius: 5,
    mouseOutPolygonStroke: COLOR.MOUSEOUT_POLYGON_STROKE,
    mouseOutPolygonStrokeWidth: 0,
    mouseOutPolygonStrokeOpacity: 1,
    mouseOutPolygonFillOpacity: 1,

    mouseEnterPolygonStroke: COLOR.MOUSEENTER_POLYGON_STROKE,
    mouseEnterPolygonStrokeWidth: 0,
    mouseEnterPolygonStrokeOpacity: 1,
    mouseEnterPolygonFillOpacity: 0.5,

    clickedPolygonStroke: COLOR.CLICK_STROKE_COLOR,
    clickedPolygonStrokeOpacity: 1,
    clickedPolygonStrokeWidth: 0
};

export interface MapProps {
    setupExtraLayers?: (map: mapboxgl.Map) => void;
    meta?: Record<string, unknown>;
    disablePolygonHoverStyles?: boolean;
    useHighlightEffectForSelection?: boolean;

    fitBoundsSettings?: {
        padding: {
            top: number;
            bottom: number;
            left: number;
            right: number;
        };
    };
    spatialValues?: SpatialValue[];
    selectedAdminId?: string;
    displayAdminIds?: string[];
    flyToAdminIds?: string[];
    className?: string;
    showLegend?: boolean;
    showTooltip?: boolean;
    colorMappings?: ColorMappings;
    legends?: Array<MapLegend>;
    layers?: DataLayer[];
    hover?: HoverCallback;
    minZoom?: number;
    maxZoom?: number;
    maxBounds?: number[];
    center?: number[];
    backButtonMeta?: BackButtonMeta;
    shouldRenderMeta?: Record<string, string>;
    scrollZoom?: boolean;
    findBounds?: (args: { propValues: string[]; propKey: string }) => Promise<{
        bbox: number[];
    }>;
    oceanLayerDisplay?: boolean;
    labelLayerDisplay?: boolean;
    onFeatureClick?: (args: {
        code: string;
        feature: Record<string, unknown>;
        value: string | number;
        data: unknown;
    }) => void;
}

export const TILENAME = {
    CONTEXT: "context-tile",
    PROVIDER: "provider-tile",
    SCHEME: "scheme-tile"
};

const tileApis = {
    base: "/australia-oceania_australia.mbtiles",
    scheme: "/cr_schemes.mbtiles",
    provider: "/cr_providers.mbtiles"
};

export const IS_SCHEME_GEOJSON = true;

const globalStyles: (args: {
    tileApi: string;
    schemeGeojson: Geojson;
    oceanLayerDisplay?: boolean;
    labelLayerDisplay?: boolean;
}) => Record<string, unknown> = ({ schemeGeojson, tileApi, oceanLayerDisplay = true, labelLayerDisplay = true }) => {
    return {
        sources: [
            {
                id: TILENAME.CONTEXT,
                type: "vector",
                tiles: [`${tileApi}${tileApis.base}/{z}/{x}/{y}.pbf`]
            },
            {
                id: TILENAME.PROVIDER,
                type: "vector",
                tiles: [`${tileApi}${tileApis.provider}/{z}/{x}/{y}.pbf`]
            },
            // {
            //     id: TILENAME.SCHEME,
            //     type: "vector",
            //     tiles: [`${tileApi}${tileApis.scheme}/{z}/{x}/{y}.pbf`]
            // },
            {
                id: TILENAME.SCHEME,
                type: "geojson",
                data: schemeGeojson
                // {
                //     type: "FeatureCollection",
                //     features: []
                // }
            }
        ],
        contextLayers: [
            {
                id: "background",
                paint: {
                    "background-color": COLOR.BACKGROUND
                },
                type: "background",
                layout: {
                    // visibility: "visible"
                    visibility: "none"
                }
            },
            {
                id: "boundary",
                type: "line",
                source: TILENAME.CONTEXT,
                "source-layer": "boundary",

                filter: ["==", "$type", "LineString"],
                layout: {
                    visibility: "none",
                    // visibility: "visible",
                    "line-join": "round",
                    "line-cap": "round"
                },
                paint: {
                    "line-color": COLOR.BACKGROUND,
                    "line-width": 1,
                    "line-opacity": 0.75
                }
            },
            {
                filter: ["==", "$type", "Polygon"],
                id: "ocean",
                paint: {
                    "fill-color": COLOR.OCEAN
                },
                source: TILENAME.CONTEXT,
                "source-layer": "water",
                type: "fill",
                layout: {
                    // visibility: "none"
                    visibility: oceanLayerDisplay === true ? "visible" : "none"
                }
            },
            {
                id: "waterway",
                type: "line",
                source: TILENAME.CONTEXT,
                "source-layer": "waterway",
                filter: ["==", "$type", "LineString"],
                layout: {
                    visibility: "none",
                    // visibility: "visible",
                    "line-join": "round",
                    "line-cap": "round"
                },
                paint: {
                    "line-color": COLOR.OCEAN,
                    "line-width": 2,
                    "line-opacity": 0.75
                }
            },
            // {
            //     id: "placeLabels",
            //     type: "symbol",
            //     filter: [
            //         "all",
            //         ["==", "$type", "Point"],
            //         [
            //             "in",
            //             "class",
            //             "village",
            //             "town",
            //             "island",
            //             "city"
            //             //"country"
            //         ]
            //     ],
            //     layout: {
            //         visibility: labelLayerDisplay === true ? "visible" : "none",
            //         "text-anchor": "center",
            //         "text-field": "{name:latin}\n{name:nonlatin}",
            //         "text-font": [
            //             "DIN Offc Pro Medium,Arial Unicode MS Regular"
            //             // "Lato, sans-serif"
            //         ],
            //         "text-max-width": 12,
            //         "text-size": {
            //             stops: [
            //                 // [3, 10],
            //                 [6, 9],
            //                 [12, 13]
            //             ]
            //         }
            //     },
            //     minzoom: 0,
            //     paint: {
            //         "text-color": "hsl(0, 0%, 25%)",
            //         "text-halo-blur": 0,
            //         "text-halo-color": "hsl(0, 0%, 100%)",
            //         "text-halo-width": 2
            //     },
            //     source: TILENAME.CONTEXT,
            //     "source-layer": "place"
            // },
            // {
            //     id: "testaaa",
            //     type: "circle",
            //     source: `${TILENAME.SCHEME}`,
            //     "source-layer": "schemes",

            //     minzoom: 4,
            //     maxzoom: 13,

            //     paint: {
            //         // "circle-radius": {
            //         //     base: 1.75,
            //         //     stops: [
            //         //         [12, 2],
            //         //         [22, 180]
            //         //     ]
            //         // },
            //         "circle-opacity": 1,
            //         "circle-radius": 5,
            //         "circle-color": "green",
            //         "circle-stroke-color": "green",
            //         "circle-stroke-opacity": 1,
            //         "circle-stroke-width": 1
            //     }

            //     // type: "fill",
            //     // source: `${TILENAME.SCHEME}-test`,
            //     // layout: {},
            //     // paint: {
            //     //     "fill-color": "#0080ff", // blue color fill
            //     //     "fill-opacity": 0.5
            //     // }
            // },
            {
                filter: ["all", ["==", "$type", "LineString"]],
                id: "waterwayLabels",
                layout: {
                    "symbol-placement": "line",
                    "text-field": "{name:latin} {name:nonlatin}",
                    "text-font": [
                        // https://api.mapbox.com/fonts/v1/mapbox/DIN Offc Pro
                        // Medium,Arial Unicode MS Regular/8192-8447.pbf?access_token=pk.eyJ1IjoibnljZG90IiwiYSI6IlhHQjNQRWMifQ.cz7P1kLgUTLOlt9Lc1RQvQ
                        // https://api.mapbox.com/fonts/v1/mapbox/Noto%20Sans%20Regular/0-255.pbf?access_token=pk.eyJ1IjoibnljZG90IiwiYSI6IlhHQjNQRWMifQ.cz7P1kLgUTLOlt9Lc1RQvQ
                        "DIN Offc Pro Medium,Arial Unicode MS Regular"
                    ],
                    "text-letter-spacing": 0.1,
                    "text-rotation-alignment": "map",
                    "text-size": {
                        base: 1.4,
                        stops: [
                            [10, 8],
                            [20, 14]
                        ]
                    },
                    "text-transform": "uppercase",
                    visibility: "none",
                    // visibility: "visible",
                    "symbol-spacing": 10,
                    "symbol-avoid-edges": false
                    // visibility: "none"
                },
                paint: {
                    "text-color": "#000",
                    "text-halo-color": "hsl(0, 0%, 100%)",
                    "text-halo-width": 2
                },
                source: TILENAME.CONTEXT,
                "source-layer": "waterway",
                type: "symbol"
            },
            {
                id: "placeLabels",
                type: "symbol",
                filter: [
                    "all",
                    ["==", "$type", "Point"],
                    [
                        "in",
                        "class",
                        "village",
                        "town",
                        "island",
                        "city"
                        //"country"
                    ]
                ],
                layout: {
                    visibility: labelLayerDisplay === true ? "visible" : "none",
                    "text-anchor": "center",
                    "text-field": "{name:latin}\n{name:nonlatin}",
                    "text-font": [
                        "DIN Offc Pro Medium,Arial Unicode MS Regular"
                        // "Lato, sans-serif"
                    ],
                    "text-max-width": 12,
                    "text-size": {
                        stops: [
                            // [3, 10],
                            [6, 9],
                            [12, 13]
                        ]
                    }
                },
                minzoom: 0,
                paint: {
                    "text-color": "hsl(0, 0%, 25%)",
                    "text-halo-blur": 0,
                    "text-halo-color": "hsl(0, 0%, 100%)",
                    "text-halo-width": 2
                },
                source: TILENAME.CONTEXT,
                "source-layer": "place"
            },
            {
                id: "pointFake",
                type: "symbol",
                filter: ["all", ["==", "$type", "Point"], ["in", "class", "village", "town", "island", "city"]],
                layout: {
                    visibility: "none"
                },
                minzoom: 0,
                paint: {},
                source: TILENAME.CONTEXT,
                "source-layer": "place"
            }
        ]
    };
};

export const BaseMap: React.FC<MapProps> = ({
    useHighlightEffectForSelection = false,

    setupExtraLayers,
    className,
    // flyTo,
    displayAdminIds = [],
    flyToAdminIds = [],
    selectedAdminId = "",
    spatialValues = [],
    showLegend = false,
    showTooltip = true,
    colorMappings = {},
    legends = [],
    layers = [],
    meta = {},
    minZoom,
    maxZoom,
    maxBounds,
    center,
    backButtonMeta,
    shouldRenderMeta,
    onFeatureClick,
    hover,
    findBounds,
    disablePolygonHoverStyles = false,
    fitBoundsSettings,
    scrollZoom = true,
    labelLayerDisplay = true,
    oceanLayerDisplay = true
}) => {
    const { scheme } = useShapefileContext();
    let chart = {
        fitBoundsSettings,
        ...{ showLegend: true, showTooltip: true, legendMappings: [] },

        ...globalStyles({ tileApi: Config.tileApi, labelLayerDisplay, oceanLayerDisplay, schemeGeojson: scheme }),
        back: backButtonMeta,
        shouldRenderMeta,
        onFeatureClick,
        findBounds,
        ...(maxBounds
            ? {
                  maxBounds: [
                      [maxBounds[0], maxBounds[1]],
                      [maxBounds[2], maxBounds[3]]
                  ]
              }
            : {}),
        layers: layers.map(({ id, type, ...rest }) => {
            return {
                id,
                type,
                ...(type === "point"
                    ? pointStyle
                    : !disablePolygonHoverStyles
                    ? polygonStyles(useHighlightEffectForSelection)
                    : disabledHoverPolygonStyles(useHighlightEffectForSelection)),
                ...rest,
                selection: displayAdminIds,
                focusSelection: flyToAdminIds,
                current: selectedAdminId,
                spatialValues,
                valueExtractedField: "category",
                idExtractedField: "adminId"
            };
        }),
        ...{
            minZoom,
            maxZoom,
            center
        },
        ...{
            colorMappings: {
                default: COLOR.NOT_FOUND_COLOR,
                ...(colorMappings ?? {})
            }
        },
        ...{
            showLegend,
            showTooltip,
            legendMappings: legends,
            meta,
            hover
        },
        ...{
            scrollZoom
        },
        setupExtraLayers
    };

    return (
        <div className={`base-map ${className ?? ""}`}>
            <JSBaseMap {...chart} />
        </div>
    );
};
